import React from 'react';
import { Link } from 'react-router-dom';
import Guilloche from './Guilloche';
import Icon from './Icon';
import PageSection from './page/PageSection';
import StepNumber from './page/StepNumber';
import { tie } from './page/typography';
import { licenceFile } from '../content/pages';

/**
 * Ліцензійна справа — малюнок і склад.
 *
 * Спершу це була секція головної. Власник прибрав її звідти — і слушно:
 * склад пакета документів до МОЗ цікавить того, хто вже дійшов до
 * ліцензування, а не кожного, хто відкрив сайт. Тепер блок стоїть на
 * сторінці послуги ліцензування (ServicePage), де людина і читає про
 * процедуру.
 *
 * Картинка тут не заради картинки: ліцензійна справа — це те, що компанія
 * насправді робить руками, і те, чого клієнт до першої зустрічі зазвичай не
 * уявляє. Аркуші розкладені віялом, і номер на кожному збігається з номером
 * у переліку праворуч: малюнок і перелік читаються як одне ціле.
 *
 * Склад справи взято з опису послуги ліцензування (content/services.js), а
 * не складено наново: у переліку рівно ті документи, про які там ідеться.
 * За аркушами — гільйошна розета, той самий візерунок, що друкують на
 * бланках ліцензій.
 */

const SHEET = { x: 176, y: 92, width: 128, height: 184, rx: 5 };
// Крок повороту підібраний так, щоб з-під переднього аркуша визирала смуга
// завширшки приблизно як кружечок із номером: менше — номери наїжджають
// один на одного, більше — віяло розсипається.
const STEP = 8.6;
const PIVOT = { x: 240, y: 372 };

const Sheet = ({ index, front }) => {
  const angle = (index - 2.5) * STEP;
  return (
    <g transform={`rotate(${angle} ${PIVOT.x} ${PIVOT.y})`}>
      <rect {...SHEET} fill="#fff" stroke="currentColor" strokeWidth="1.4" />

      {front && (
        <>
          <path
            d="M196 150h88M196 166h88M196 182h60"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
          />
          {/* Печатка — те, чим справа закінчується. */}
          <circle cx="240" cy="228" r="21" fill="none" stroke="currentColor" strokeWidth="1.4" />
          <circle cx="240" cy="228" r="9" fill="none" stroke="currentColor" strokeWidth="1" />
          <path
            d="M229 246v22l11-8 11 8v-22"
            fill="#fff"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinejoin="round"
          />
        </>
      )}

      <circle cx="284" cy="114" r="13" fill="#fff" stroke="currentColor" strokeWidth="1.4" />
      <text
        x="284"
        y="115"
        textAnchor="middle"
        dominantBaseline="central"
        fontSize="12"
        fontWeight="700"
        fill="currentColor"
        className="font-display"
      >
        {String(index + 1)}
      </text>
    </g>
  );
};

const LicenceFileDrawing = ({ className }) => (
  // viewBox обрізаний по самому віялу, а не по колу повороту: інакше третина
  // висоти малюнка — це порожнє поле над аркушами, і в розкладці він
  // «висить» замість того, щоб стояти поруч із переліком.
  <svg viewBox="70 75 344 242" className={className} aria-hidden="true" focusable="false">
    {/* Аркуші малюються з останнього до першого, щоб перший лежав зверху:
        у SVG порядок у розмітці і є порядком накладання. */}
    {[5, 4, 3, 2, 1, 0].map((index) => (
      <Sheet key={index} index={index} front={index === 0} />
    ))}
  </svg>
);

const LicenceFile = () => (
  <PageSection
    id="licenzijna-sprava"
    caption="Склад справи"
    title="Ліцензійна справа: що саме подається до МОЗ"
    description="Ліцензія видається не «клініці» взагалі, а конкретному суб’єкту господарювання з конкретним пакетом документів. Ось із чого цей пакет складається."
  >
    <div className="grid gap-8 lg:gap-12 lg:grid-cols-2 lg:items-start">
      <div className="hidden sm:block relative rounded-2xl bg-white border border-slate-200 overflow-hidden p-8 sm:p-10 lg:p-12">
        <Guilloche className="absolute -top-28 -right-28 w-96 h-96 text-teal-500 opacity-[0.12] pointer-events-none" />
        <LicenceFileDrawing className="relative w-full max-w-md mx-auto text-teal-700" />
        <div className="relative mt-8 pt-5 border-t border-slate-200 flex items-center justify-between text-[0.7rem] uppercase tracking-[0.2em]">
          <span className="text-slate-500">Комплект до МОЗ</span>
          <span className="text-teal-700">6 документів</span>
        </div>
      </div>

      <div>
        <ol className="list-none divide-y divide-slate-200 border-y border-slate-200">
          {licenceFile.map((item, index) => (
            <li key={item.label} className="py-4 flex items-start gap-4">
              {/* pt-1 ставить перший рядок назви на рівень центру кружка. */}
              <StepNumber n={index + 1} size="sm" />
              <div className="pt-1">
                <h3 className="font-bold text-slate-900 font-display leading-snug">{item.label}</h3>
                <p className="text-[0.95rem] text-slate-600 leading-relaxed mt-0.5">{tie(item.text)}</p>
              </div>
            </li>
          ))}
        </ol>

        {/* Стрілка — у рядку тексту через нерозривний пробіл, а не окремим
            флекс-елементом: на телефоні підпис переноситься, і стрілка
            відривалася до правого краю. py-3 -my-3 — ціль для пальця 44px. */}
        <Link
          to="/plan-zapusku-kliniky"
          className="mt-3 inline-block py-3 -mb-3 text-teal-700 font-semibold hover:text-teal-800 transition-colors duration-200"
        >
          Де ці документи з’являються у плані запуску{'\u00a0'}<Icon name="fa-arrow-right" />
        </Link>
      </div>
    </div>
  </PageSection>
);

export default LicenceFile;
